import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Spinner } from 'react-bootstrap';
import {
  normalizeAccreditationPhotoForFormio,
  getAccreditationPhotoDisplayUrls,
} from '../../utils/emsPhotoUtils';
import AccreditationPhotoManager from '../user/AccreditationPhotoManager';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import '../../components/css/FormioOverrides.css';
import '../css/UserFormHeader.css';
import FormioTabRenderer from './FormioTabRenderer';
/* After FormioTabRenderer so Personal Settings radio layout beats UserFormView :has() */
import '../css/ParticipantEdit.css';
/* After Validation so Personal Settings layout wins cascade */
import '../css/ParticipantEditValidation.css';
import '../css/ParticipantEditPersonalSettings.css';
import { forceFormioShowValidationErrors } from '../../utils/formioSubmissionNormalize';
import {
  formSchemaHasCaddieSelect,
  normalizeDataGridForFormioRender,
  copyCaddieFieldsFromSource,
  personDataHasCaddieFields,
  preserveAndNormalizeCaddiesOnSave,
} from '../../utils/caddieFormioUtils';
import {
  buildInitialFormData,
  filterPersonDataForFormSave,
  preserveParticipantIdentityFromPerson,
  collectFormDataKeys,
} from '../../utils/formSchemaUtils';
import { isInvitationTypesFormName, formatAccreditationCategoryLabel } from '../../utils/invitationTypeFormioUtils';
import {
  generateInvitePassword,
  getOrCreateInvitePassword,
  normalizePersonId,
} from '../../utils/invitePasswordCache';
import axios from 'axios';
import axiosAuth from '../../api/axiosInstance';
import mergeWith from 'lodash/mergeWith';
import cloneDeep from 'lodash/cloneDeep';
import AccommodationSection from './AccommodationSection';
import ParticipantBookingTab from './ParticipantBookingTab';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faClock, faPen, faUser } from '@fortawesome/free-solid-svg-icons';
import { resolvePersonImageUrl } from '../../utils/resolvePersonImageUrl';
import { calculateVisibleFieldProgress } from '../../utils/formVisibleProgress';
import { tabPercentFromProgress } from '../user/UserFormPrototypeHeader';

const TABS = ['REGISTRATION', 'ACCREDITATION', 'TRAVEL', 'ACCOMMODATION', 'GUEST', 'PERSONAL SETTINGS', 'SEND EMAIL', 'BOOKING'];

/** Display label — sentence case like Participant Edit Prototype (REGISTRATION → Registration). */
const tabDisplayLabel = (tab) =>
  tab ? tab.charAt(0) + tab.slice(1).toLowerCase() : '';

/** Map PE tab → progress_breakdown segment (same keys as user portal where possible). */
const TAB_PROGRESS_KEY = {
  REGISTRATION: 'registration',
  ACCREDITATION: 'accreditation',
  TRAVEL: 'travel',
  ACCOMMODATION: 'accommodation',
  GUEST: 'guest',
  'PERSONAL SETTINGS': 'personalSettings',
};

const participantFormTabClass = (tab) =>
  `participant-form-tab participant-form-tab-${String(tab).replace(/\s+/g, '-')}`;
/** Tabs without a Form.io form (no save on switch, no Submit button). */
const NO_FORM_TABS = ['SEND EMAIL', 'BOOKING'];
const TABS_REQUIRING_DATA = TABS.filter((t) => !NO_FORM_TABS.includes(t));

/** Same form IDs as UserFormView — one form per tab (do not map by API list index). */
const FORM_TAB_IDS = {
  REGISTRATION: '68c7eefbd44999eedb541043',
  ACCREDITATION: '68e6505866f25479291b933f',
  TRAVEL: '68e781408e1384b2c283c3af',
  ACCOMMODATION: '68e784018e1384b2c283c3bb',
  GUEST: '68e785cb8e1384b2c283c3c5',
};

/** Participant-edit profile upload endpoint (EMS uploads; independent from accreditation form photo URL) */
const getParticipantEditUploadUrl = (API_URL) => {
  const base = (API_URL || '').replace(/\/$/, '');
  return base ? `${base}/ems/uploads` : '/api/ems/uploads';
};


const ParticipantEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const API_URL = process.env.REACT_APP_API_URL || '';

  const getInitialTab = () => {
    const params = new URLSearchParams(location.search);
    const tabParam = (params.get('tab') || '').toLowerCase();
    if (tabParam === 'email') return 'SEND EMAIL';
    // Permissions menu → Personal settings (invitation types) form
    if (tabParam === 'personal-settings' || tabParam === 'permissions' || tabParam === 'personal') {
      return 'PERSONAL SETTINGS';
    }
    // Accommodation menu → Accommodation form
    if (tabParam === 'accommodation' || tabParam === 'accomodation') {
      return 'ACCOMMODATION';
    }
    return TABS[0];
  };

  const [participant, setParticipant] = useState(null);
  /** Stable tab → formId map (PERSONAL SETTINGS filled in after name resolve). */
  const [forms, setForms] = useState(() => ({ ...FORM_TAB_IDS }));
  const [personalSettingsResolving, setPersonalSettingsResolving] = useState(true);
  /** Bumps when a form schema is cached — forces FormioTabRenderer to see initialSchema. */
  const [schemaCacheTick, setSchemaCacheTick] = useState(0);
  const schemaCacheRef = useRef({});
  const [loading, setLoading] = useState(true);
  const hasLoadedOnce = useRef(false);
  /** Prevents overlapping tab-switch background saves. */
  const tabSwitchSaveInFlightRef = useRef(false);
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [registrationStatus, setRegistrationStatus] = useState('');
  const [approvalStatus, setApprovalStatus] = useState('Pending');
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [invitationTypes, setInvitationTypes] = useState([]);
  const [selectedInvitationType, setSelectedInvitationType] = useState('');
  const [emsRoles, setEmsRoles] = useState([]);
  const [selectedEmsRole, setSelectedEmsRole] = useState('');
  const [accreditationCategories, setAccreditationCategories] = useState([]);
  const [selectedAccreditationCategory, setSelectedAccreditationCategory] = useState('');
  const [profilePhoto, setProfilePhoto] = useState('');
  const [bookingGroups, setBookingGroups] = useState([]);
  /** Tab completion % — same shape as user portal progress_breakdown. */
  const [tabProgress, setTabProgress] = useState({});
  const tabsTrackRef = useRef(null);
  const tabSliderRef = useRef(null);
  const tabBtnRefs = useRef({});
  const fileInputRef = useRef(null);
  /** Latest Form.io submission.data for the active tab (may be ahead of React state when onChange skips parent). */
  const latestFormDataRef = useRef(null);
  /** Active tab Form.io schema components — used to filter person_data on save. */
  const activeFormSchemaRef = useRef(null);
  /** Set by FormioTabRenderer — call before save to snapshot live Webform data. */
  const flushLiveFormDataRef = useRef(() => {});
  /** Live Form.io webform — used to read default caddie radio on save. */
  const formioWebformRef = useRef(null);
  /** CRM caddie lookup — synced from FormioTabRenderer when registration form loads. */
  const caddiesByIdRef = useRef(new Map());
  const hasRegistrationCaddieRef = useRef(false);
  /** Tab switch: quiet save (no full validation). */
  const triggerSaveRef = useRef(() => Promise.resolve(true));
  /** Footer Submit: formio.submit() with validation. */
  const triggerFormSubmitRef = useRef(() => Promise.resolve());
  /** Set by FormioTabRenderer — clears Form.io error panels after successful save. */
  const clearFormErrorsRef = useRef(() => {});
  /** Prevents handleFormSave firing multiple times for a single submit (Form.io fires onSubmit per panel). */
  const isSavingRef = useRef(false);
  const pageMessageTimerRef = useRef(null);
  /** Skip submissionVersion bump on first selectedGroup sync from fetch. */
  const selectedGroupInitializedRef = useRef(false);
  /** Tab label at save time (avoids wrong message if activeTab changes before async save finishes). */
  const activeTabRef = useRef(activeTab);

  /** Display name only — SMTP auth always uses SMTP_USER / SMTP_PASS from server .env */
  const [fromName] = useState('IGF Team');

  const quillRef = useRef(null);
  const [editorHtml, setEditorHtml] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateSubject, setTemplateSubject] = useState('');
  /** Generated at template load; injected into body only on send (not shown in editor) */
  const [invitePlainPassword, setInvitePlainPassword] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [templateFiles, setTemplateFiles] = useState([]);
  const [placeholders] = useState(['{{name}}', '{{email}}', '{{phone}}', '{{competition}}', '{{password}}']);
  const [submissionVersion, setSubmissionVersion] = useState(0);
  /** Incremented after a bust-cache fetch on the Accreditation tab; forces FormioTabRenderer remount. */
  const [accreditationKey, setAccreditationKey] = useState(0);
  /** Ref forwarded to AccreditationPhotoManager so we can call openCrop/openView from the portal button. */
  const photoManagerRef = useRef(null);
  /** DOM node of the Formio photo field container — used as portal target. */
  const [photoFieldPortalEl, setPhotoFieldPortalEl] = useState(null);
  const tabContentRef = useRef(null);
  /** Same pattern as UserLogin: { type, text } or failed save validation { type, text, validationItems } */
  const [pageMessage, setPageMessage] = useState(null);
  /** Reveal Form.io required-field highlights after failed Save (same as user portal). */
  const [showInlineErrors, setShowInlineErrors] = useState(false);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  /** When admin changes group, remount Form.io with updated groupName / Group_id for all participant types. */
  useEffect(() => {
    if (!participant?._id) return;
    if (!selectedGroupInitializedRef.current) {
      selectedGroupInitializedRef.current = true;
      return;
    }
    setSubmissionVersion((v) => v + 1);
  }, [selectedGroup, participant?._id]);

  /* --------------------- Utilities --------------------- */
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = new Intl.DateTimeFormat('en-GB', { month: 'short' }).format(date);
    const year = date.getFullYear();
    
    return `${day}-${month}-${year}`;
  };

  /* --------------------- Fetch templates --------------------- */
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await axios.get(`${API_URL}/ems/templates`);
        setTemplates(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to fetch templates', err);
      }
    };
    fetchTemplates();
  }, [API_URL]);

  /* --------------------- Fetch groups --------------------- */
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await axios.get(`${API_URL}/ems/groups`);
        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setGroups(data);
      } catch (err) {
        console.error('Failed to fetch groups', err);
      }
    };
    fetchGroups();
  }, [API_URL]);

  /* --------------------- Fetch invitation types --------------------- */
  useEffect(() => {
    const fetchInvitationTypes = async () => {
      try {
        const res = await axios.get(`${API_URL}/ems/invitation-types?all=true`);
        setInvitationTypes(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to fetch invitation types', err);
      }
    };
    fetchInvitationTypes();
  }, [API_URL]);

  /* --------------------- Fetch EMS roles --------------------- */
  useEffect(() => {
    const fetchEmsRoles = async () => {
      try {
        const res = await axios.get(`${API_URL}/ems/roles`);
        setEmsRoles(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to fetch EMS roles', err);
      }
    };
    fetchEmsRoles();
  }, [API_URL]);

  /* --------------------- Fetch accreditation categories --------------------- */
  useEffect(() => {
    const fetchAccreditationCategories = async () => {
      try {
        const res = await axios.get(`${API_URL}/ems/accreditation-categories`);
        setAccreditationCategories(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to fetch accreditation categories', err);
      }
    };
    fetchAccreditationCategories();
  }, [API_URL]);

  /* Invitation Types Form has no stable ID — resolve by name; never wipe FORM_TAB_IDS. */
  useEffect(() => {
    let cancelled = false;
    const resolveInvitationTypesForm = async () => {
      setPersonalSettingsResolving(true);
      try {
        const res = await axios.get(`${API_URL}/ems/forms`);
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const match = list.find((f) => isInvitationTypesFormName(f?.form_name));
        if (match?._id) {
          setForms((prev) => ({ ...prev, 'PERSONAL SETTINGS': String(match._id) }));
        }
      } catch (err) {
        console.error('Failed to resolve Invitation Types form', err);
      } finally {
        if (!cancelled) setPersonalSettingsResolving(false);
      }
    };
    resolveInvitationTypesForm();
    return () => { cancelled = true; };
  }, [API_URL]);

  /* Prefetch every tab schema once — tab switches must not wait on network. */
  useEffect(() => {
    if (!participant?._id || !API_URL) return;
    let cancelled = false;
    const ids = [...new Set(Object.values(forms).filter(Boolean))];
    ids.forEach(async (formId) => {
      if (schemaCacheRef.current[formId]?.schema?.components) return;
      try {
        const res = await axios.get(`${API_URL}/ems/forms/${formId}`);
        if (cancelled || !res.data?.schema?.components) return;
        schemaCacheRef.current[formId] = {
          schema: res.data.schema,
          form_name: res.data.form_name || '',
        };
        setSchemaCacheTick((t) => t + 1);
      } catch (err) {
        console.warn('Prefetch form schema failed:', formId, err?.message || err);
      }
    });
    return () => { cancelled = true; };
  }, [participant?._id, forms, API_URL]);

  /**
   * Recompute ALL tab % from person_data + cached schemas (same math as user portal).
   * Skips the active tab when live Form.io already reported progress (avoids stomping mid-edit).
   */
  useEffect(() => {
    if (!participant?._id || !schemaCacheTick) return;
    const personData = safeParsePersonData(participant.person_data);
    const activeSegment = TAB_PROGRESS_KEY[activeTab];
    setTabProgress((prev) => {
      const next = { ...prev };
      let changed = false;
      Object.entries(TAB_PROGRESS_KEY).forEach(([tab, segment]) => {
        const formId = forms[tab];
        if (!formId) return;
        const components = schemaCacheRef.current[formId]?.schema?.components;
        if (!Array.isArray(components) || !components.length) return;
        /* Keep live active-tab stats when Form.io already counted fields. */
        if (
          segment === activeSegment &&
          prev[segment] &&
          Number(prev[segment].total) > 0
        ) {
          return;
        }
        const { filled, total } = calculateVisibleFieldProgress(
          personData,
          components,
          null
        );
        const prevSeg = prev[segment];
        if (
          Number(prevSeg?.total) === total &&
          Number(prevSeg?.completed) === filled
        ) {
          return;
        }
        next[segment] = {
          total,
          completed: filled,
          display: `${filled}/${total}`,
        };
        changed = true;
      });
      return changed ? next : prev;
    });
    // activeTab intentionally omitted — only recompute when data/schemas change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participant?._id, participant?.person_data, forms, schemaCacheTick]);

  /* --------------------- Fetch Accommodation Bookings --------------------- */
  useEffect(() => {
    const personOid = participant?.person_id?._id;
    if (activeTab !== 'ACCOMMODATION' || !personOid) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/ems/room-bookings/grouped?participant_id=${personOid}`
        );
        if (!cancelled) setBookingGroups(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Error fetching accommodation bookings:', err);
        if (!cancelled) setBookingGroups([]);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, participant?.person_id?._id, API_URL]);

  /* --------------------- Fetch participant (shared so we can refetch after save) --------------------- */
  const fetchParticipantData = React.useCallback(async (bustCache = false) => {
    if (!id) return;
    const requestId = id;
    // Only show the full-page loading overlay on the very first load.
    // Subsequent background fetches (e.g. after save) must not unmount FormioTabRenderer.
    if (!hasLoadedOnce.current) setLoading(true);
    try {
      const url = bustCache
        ? `${API_URL}/ems/participants/${requestId}?_=${Date.now()}`
        : `${API_URL}/ems/participants/${requestId}`;
      const res = await axios.get(url);
      if (requestId !== id) return;

      const p = res.data;
      const cleanData = safeParsePersonData(p?.person_data);

      setParticipant(p || null);
      if (p?.progress_breakdown && typeof p.progress_breakdown === 'object') {
        setTabProgress((prev) => {
          const next = { ...prev };
          Object.entries(p.progress_breakdown).forEach(([segment, db]) => {
            if (!db || typeof db !== 'object') return;
            const local = prev[segment];
            /* Prefer fresher local counts when DB is still 0/stale (same as PanelDataService). */
            if (
              local &&
              Number(local.total) > 0 &&
              Number(local.completed) > Number(db.completed || 0)
            ) {
              return;
            }
            next[segment] = {
              total: Number(db.total) || 0,
              completed: Number(db.completed) || 0,
            };
          });
          return next;
        });
      }

      // profilePhoto tracks only the separate "editProfilePhoto" upload field (header button).
      const editPhoto = cleanData.editProfilePhoto;
      const editRef = Array.isArray(editPhoto) && editPhoto[0]
        ? (typeof editPhoto[0] === 'object' ? (editPhoto[0].url ?? '') : editPhoto[0])
        : (editPhoto && typeof editPhoto !== 'object' ? editPhoto : '');
      setProfilePhoto(String(editRef || ''));
      setRegistrationStatus(p?.registration_status || '');
      setApprovalStatus(p?.approval_status || 'Pending');

      const groupVal = p?.group_id
        ? typeof p.group_id === 'object'
          ? String(p.group_id._id || '')
          : String(p.group_id)
        : '';
      setSelectedGroup(groupVal);
      const invitationVal = p?.invitation_type_id
        ? typeof p.invitation_type_id === 'object'
          ? String(p.invitation_type_id._id || '')
          : String(p.invitation_type_id)
        : '';
      setSelectedInvitationType(invitationVal);
      if (p?.invitation_type_id && typeof p.invitation_type_id === 'object' && p.invitation_type_id._id) {
        setInvitationTypes(prev => {
          const id = String(p.invitation_type_id._id);
          if (prev.some(t => String(t._id) === id)) return prev;
          return [...prev, p.invitation_type_id];
        });
      }
      const roleVal = p?.role_id
        ? typeof p.role_id === 'object'
          ? String(p.role_id._id || '')
          : String(p.role_id)
        : '';
      setSelectedEmsRole(roleVal);
      const accreditationVal = p?.accreditation_category_id
        ? typeof p.accreditation_category_id === 'object'
          ? String(p.accreditation_category_id._id || '')
          : String(p.accreditation_category_id)
        : '';
      setSelectedAccreditationCategory(accreditationVal);
      latestFormDataRef.current = personDataHasCaddieFields(cleanData)
        ? normalizeDataGridForFormioRender(JSON.parse(JSON.stringify(cleanData)))
        : cleanData;
      selectedGroupInitializedRef.current = false;
      // Do NOT bump submissionVersion here — every bump remounts Form.io (skeleton flash / blank form).
      // Callers that need a remount (save, photo crop) bump explicitly.
    } catch (err) {
      console.error('Error fetching participant data:', err);
    } finally {
      if (requestId === id) {
        hasLoadedOnce.current = true;
        setLoading(false);
      }
    }
  }, [id, API_URL]);

  useEffect(() => {
    if (id) fetchParticipantData();
  }, [id, fetchParticipantData]);

  /* Prototype tab slider — mutate DOM left/width (exact HTML prototype), not React state.
   * Must re-run after loading ends (tabs mount) or pill stays at 0 and labels go white-on-white. */
  const positionTabSlider = useCallback(() => {
    const track = tabsTrackRef.current;
    const slider = tabSliderRef.current;
    const btn = tabBtnRefs.current[activeTab];
    if (!track || !slider || !btn) return;
    const trackRect = track.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    if (btnRect.width === 0) {
      window.setTimeout(positionTabSlider, 50);
      return;
    }
    const firstPaint = !track.hasAttribute('data-slider-ready');
    if (firstPaint) slider.style.transition = 'none';
    /* Same math as prototype positionSlider() — no scrollLeft double-count */
    slider.style.top = `${btnRect.top - trackRect.top}px`;
    slider.style.height = `${btnRect.height}px`;
    slider.style.left = `${btnRect.left - trackRect.left}px`;
    slider.style.width = `${btnRect.width}px`;
    if (firstPaint) {
      void slider.offsetWidth;
      slider.style.transition = '';
      track.setAttribute('data-slider-ready', 'true');
    }
  }, [activeTab]);

  useLayoutEffect(() => {
    if (loading || !participant) return undefined;
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      positionTabSlider();
    };
    const track = tabsTrackRef.current;
    const alreadyReady = track?.hasAttribute('data-slider-ready');
    if (alreadyReady) {
      /* Tab switch: measure before paint so pill starts sliding under white label */
      run();
    } else {
      /* First mount: double rAF like prototype (layout settle) */
      requestAnimationFrame(() => {
        requestAnimationFrame(run);
      });
    }
    const onResize = () => positionTabSlider();
    window.addEventListener('resize', onResize);
    track?.addEventListener('scroll', onResize);
    let ro;
    if (typeof ResizeObserver !== 'undefined' && track) {
      ro = new ResizeObserver(onResize);
      ro.observe(track);
    }
    return () => {
      cancelled = true;
      window.removeEventListener('resize', onResize);
      track?.removeEventListener('scroll', onResize);
      ro?.disconnect();
    };
  }, [positionTabSlider, tabProgress, loading, participant?._id]);

  /** Reset cached form state when navigating to a different participant (same route, new :id). */
  useEffect(() => {
    latestFormDataRef.current = null;
    selectedGroupInitializedRef.current = false;
    hasLoadedOnce.current = false;
    setAccreditationKey((k) => k + 1);
  }, [id]);

  /* Soft refresh when Accreditation opens — do NOT remount Form.io (that killed tab loads). */
  useEffect(() => {
    if (activeTab !== TABS[1]) return;
    fetchParticipantData(true);
  }, [activeTab, fetchParticipantData]);

  /* Called by AccreditationPhotoManager after a successful crop/save on the admin side. */
  const handlePhotoManagerUpdate = React.useCallback(() => {
    fetchParticipantData(true).then(() => {
      setAccreditationKey((k) => k + 1);
      setSubmissionVersion((v) => v + 1);
    });
  }, [fetchParticipantData]);

  /* Watch the tab content div for Formio's photo field and store it as a portal target. */
  useEffect(() => {
    if (activeTab !== TABS[1]) { setPhotoFieldPortalEl(null); return; }
    const container = tabContentRef.current;
    if (!container) return;
    const find = () => {
      // Thumbnail column only — portaled buttons sit under the image (layout from FormioOverrides.css).
      const img = container.querySelector('.formio-component-photo img');
      setPhotoFieldPortalEl(img?.parentElement || null);
    };
    find();
    const obs = new MutationObserver(find);
    obs.observe(container, { childList: true, subtree: true });
    return () => { obs.disconnect(); setPhotoFieldPortalEl(null); };
  }, [activeTab, accreditationKey]);

  /* --------------------- Helpers --------------------- */
  /** Preview: hidePassword keeps {{password}}. Send: inject plain; server stores bcrypt hash. */
  const replacePlaceholders = (templateBody, person, password, { hidePassword = false } = {}) => {
    if (!templateBody) return '';
    const firstName = person?.name || '';
    const lastName = person?.lname || '';
    const fullName = `${firstName} ${lastName}`.trim();

    let html = templateBody
      .replace(/{{name}}/g, firstName)
      .replace(/{{lname}}/g, lastName)
      .replace(/{{fullname}}/g, fullName)
      .replace(/{{email}}/g, person?.email || '')
      .replace(/{{phone}}/g, person?.phone || person?.phone1 || person?.phone2 || '')
      .replace(/{{competition}}/g, person?.competition || '');
    if (!hidePassword) {
      html = html.replace(/{{password}}/g, password || '');
    }
    return html;
  };

  const safeParsePersonData = (rawData) => {
  if (!rawData) return {};
  
  let processed = rawData;

  // 1. Loop to handle double/triple stringification
  while (typeof processed === 'string') {
    try {
      const parsed = JSON.parse(processed);
      
      // 2. Check for the "Character Map" corruption
      // if parsed is an object and looks like {"0": "{", "1": "..."}
      if (parsed && typeof parsed === 'object' && parsed["0"] === "{") {
        processed = Object.values(parsed).join("");
      } else {
        processed = parsed;
      }
    } catch (e) {
      // If it's a string but not valid JSON (like a plain ID), return empty object
      return {};
    }
  }

  // 3. Final check: Ensure we return an object, never null or a string
  return (processed && typeof processed === 'object') ? processed : {};
};

  const initialSubmission = useMemo(() => {
  if (!participant) return { data: {} };

  const person = participant.person_id || {};
  const savedData = safeParsePersonData(participant.person_data);
  const groupId = participant.group_id;
  const groupIdVal =
    savedData.Group_id != null && savedData.Group_id !== ""
      ? String(savedData.Group_id)
      : groupId
        ? typeof groupId === "object"
          ? String(groupId._id || "")
          : String(groupId)
        : "";
  const groupFromHeader = groups.find((g) => String(g._id) === String(selectedGroup));
  const groupNameVal =
    groupFromHeader?.group_name != null
      ? String(groupFromHeader.group_name)
      : savedData.groupName != null && savedData.groupName !== ""
        ? String(savedData.groupName)
        : groupId?.group_name != null
          ? String(groupId.group_name)
          : "";

  const data = buildInitialFormData({
    savedPersonData: savedData,
    groupId: selectedGroup || groupIdVal,
    groupName: groupNameVal,
    personRecord: person,
  });
  // Person record wins for core identity in admin edit (guards against cloned person_data).
  if (person._id) {
    if (person.name) data.givenFirstName = person.name;
    if (person.lname) data.familyLastName = person.lname;
    if (person.email) data.emailAddress = person.email;
    data.personId = String(person._id);
  }
  if (data.photo != null) {
    data.photo = normalizeAccreditationPhotoForFormio(data.photo, API_URL);
  }
  // Sync the participant's assigned invitation type into the Form.io field
  if (selectedInvitationType) {
    data.invitationTypes = selectedInvitationType;
  }

  try {
    return {
      data: normalizeDataGridForFormioRender(JSON.parse(JSON.stringify(data))),
    };
  } catch {
    return { data: normalizeDataGridForFormioRender({ ...data }) };
  }
}, [participant?._id, participant?.person_id, participant?.person_data, participant?.group_id, selectedGroup, selectedInvitationType, groups, API_URL]);

  /** Same source as Formio + UserFormView: submission.data.photo[0] */
  const accreditationPhotoUrls = useMemo(
    () =>
      activeTab === TABS[1]
        ? getAccreditationPhotoDisplayUrls(initialSubmission?.data, API_URL)
        : { currentPhotoUrl: null, originalPhotoUrl: null },
    [activeTab, initialSubmission, API_URL]
  );

  /** Live edits stay in latestFormDataRef (FormioTabRenderer) — do not setParticipant
   *  on every keystroke or parent re-renders remount/reset the form. */
  const handleFormChange = (tab, newData) => {
    const fields = newData?.data;
    if (!fields || typeof fields !== 'object') return;
    const existing =
      latestFormDataRef.current && typeof latestFormDataRef.current === 'object'
        ? latestFormDataRef.current
        : {};
    let next;
    try {
      next = {
        ...existing,
        ...JSON.parse(JSON.stringify(fields)),
      };
    } catch {
      next = { ...existing, ...fields };
    }
    latestFormDataRef.current = next;

    const segment = TAB_PROGRESS_KEY[tab];
    const schema = activeFormSchemaRef.current;
    if (segment && Array.isArray(schema) && schema.length) {
      const { filled, total } = calculateVisibleFieldProgress(
        next,
        schema,
        formioWebformRef.current
      );
      setTabProgress((prev) => ({
        ...prev,
        [segment]: { total, completed: filled },
      }));
    }
  };

  const clearValidationMessage = () => {
    setPageMessage((prev) => (prev?.validationItems?.length ? null : prev));
  };

  /** Same as user portal onError — block save, highlight required fields, scroll to first. */
  const handleValidationErrors = React.useCallback(() => {
    isSavingRef.current = false;
    setShowInlineErrors(true);
    if (pageMessageTimerRef.current) clearTimeout(pageMessageTimerRef.current);
    setPageMessage({
      type: 'error',
      text: 'Please complete the highlighted required fields, then try Save again.',
    });
    pageMessageTimerRef.current = setTimeout(() => setPageMessage(null), 4000);

    /* Re-mark after React paints show-errors class — re-render can wipe Form.io DOM marks. */
    const reapply = () => {
      const fi = formioWebformRef.current;
      if (fi) forceFormioShowValidationErrors(fi);
      const firstInvalid = tabContentRef.current?.querySelector(
        '.formio-component.has-error, .formio-component:has(.is-invalid), .form-control.is-invalid, .choices.is-invalid'
      );
      firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    window.requestAnimationFrame(() => {
      reapply();
      window.setTimeout(reapply, 50);
    });
  }, []);

  /* Same as user portal / GuestEdit: validate on submit only — no eager empty-field red marks. */
  const participantFormOptions = useMemo(
    () => ({ noAlerts: true, highlightErrors: false }),
    []
  );

  /**
   * Switch tabs immediately so the next form always loads.
   * Quiet-save the leaving tab in the background (never block navigation).
   * Capture schema + data BEFORE switch — activeFormSchemaRef changes after remount.
   */
  const handleTabChange = (tab) => {
    if (tab === activeTab) return;

    clearValidationMessage();
    setShowInlineErrors(false);
    clearFormErrorsRef.current?.();

    const leavingTab = activeTab;
    const leavingSchema = activeFormSchemaRef.current;
    flushLiveFormDataRef.current?.();
    let snapshot = {};
    try {
      snapshot = cloneDeep(latestFormDataRef.current || {});
    } catch {
      snapshot = { ...(latestFormDataRef.current || {}) };
    }

    setActiveTab(tab);

    // Background quiet-save on every tab switch is disabled entirely.
    // Travel/Accommodation/Guest share one person_data.dataGrid row, and
    // multiple independent code paths that run during save (the client-side
    // merge, and separately preserveAndNormalizeCaddiesOnSave for any
    // participant with caddie fields — e.g. Registration tab saves for a
    // Caddie-group participant) can each rewrite that shared array from a
    // stale snapshot, clobbering fields owned by a different tab. Scoping
    // the disable to just the three sharing tabs was not enough, since the
    // caddie-sync path can fire from Registration too. Until the shared-row
    // model changes, every tab requires an explicit Save.
    if (false) {
      tabSwitchSaveInFlightRef.current = true;
      Promise.resolve(
        handleFormSave(snapshot, {
          viaTabSwitch: true,
          tabOverride: leavingTab,
          schemaOverride: leavingSchema,
        })
      )
        .catch((err) => {
          console.warn('Background tab save failed:', err);
        })
        .finally(() => {
          tabSwitchSaveInFlightRef.current = false;
        });
    }
  };

  const handleSubmit = async () => {
    if (activeTab === 'SEND EMAIL' || !forms[activeTab]) return;
    clearValidationMessage();
    /* Do NOT clear Form.io errors before validate — that wiped is-invalid marks. */
    isSavingRef.current = false;
    const submitFn = triggerFormSubmitRef.current;
    if (typeof submitFn === 'function') {
      await submitFn();
    }
  };

  const handleCancel = () => navigate(-1);

  const handlePhotoUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  setPageMessage(null);

  const formData = new FormData();
  formData.append('file', file);

  const uploadUrl = getParticipantEditUploadUrl(API_URL);
  try {
    const uploadRes = await axios.post(uploadUrl, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    const photoUrl = uploadRes.data?.url;
    if (!photoUrl) {
      setPageMessage({ type: "error", text: "Upload did not return a file URL. Please try again." });
      return;
    }
    const filename = typeof photoUrl === 'string' ? photoUrl.split('/').pop() : photoUrl;

    const currentData = safeParsePersonData(participant?.person_data);
    const profileAsFormioFile = [{ url: filename }];
    const updatedPersonData = {
      ...currentData,
      editProfilePhoto: profileAsFormioFile,
    };
    setProfilePhoto(filename);
    setParticipant(prev => ({
      ...prev,
      person_data: updatedPersonData
    }));

    if (fileInputRef.current) fileInputRef.current.value = '';

    setPageMessage({
      type: "success",
      text: "Photo added. Switch tab or use Submit to save.",
    });
  } catch (err) {
    console.error("Upload failed:", err);
    setPageMessage({ type: "error", text: "Image upload failed. Please try again." });
  }
};

  /**
   * Called by FormioTabRenderer onSubmit — Form.io validated (same flow as UserFormView).
   * @returns {Promise<boolean>}
   */
  const handleFormSave = async (formData, saveOptions = {}) => {
    if (!participant) return false;
    const viaTabSwitch = saveOptions.viaTabSwitch === true;
    /** Form.io may fire onSubmit once per panel; only the first explicit Submit runs the API. */
    if (isSavingRef.current) {
      return viaTabSwitch ? null : true;
    }
    isSavingRef.current = true;
    const tabAtSave = saveOptions.tabOverride || activeTabRef.current;

    if (!viaTabSwitch) {
      clearTimeout(pageMessageTimerRef.current);
      setPageMessage({ type: "success", text: `Saving ${tabAtSave}…` });
    }

    flushLiveFormDataRef.current?.();

    let effectiveFormData =
      formData &&
      typeof formData === "object" &&
      !Array.isArray(formData) &&
      Object.keys(formData).length > 0
        ? cloneDeep(formData)
        : cloneDeep(latestFormDataRef.current || {});

    const schemaForSave = saveOptions.schemaOverride || activeFormSchemaRef.current;
    const isRegistrationSave = tabAtSave === "REGISTRATION";
    const hasCaddie =
      isRegistrationSave &&
      (caddiesByIdRef.current.size > 0 ||
        formSchemaHasCaddieSelect(schemaForSave || []) ||
        hasRegistrationCaddieRef.current);

    const saved = safeParsePersonData(participant.person_data);
    const groupDoc = groups.find((g) => String(g._id) === String(selectedGroup));

    // Travel/Accommodation/Guest all share one dataGrid row. A background
    // tab-switch save carries whatever the leaving tab's Form.io instance had
    // captured for the WHOLE shared row (including other tabs' fields, which
    // may be stale/blank there) — merging that in wholesale would blank
    // fields this tab never touched. Scope each tab's row writes to its own
    // schema's keys, and merge into the existing row instead of replacing it.
    const dataGridOwnKeys = schemaForSave ? collectFormDataKeys(schemaForSave) : null;
    const mergeDataGridRowsClient = (existingRows, incomingRows) => {
      const existing = Array.isArray(existingRows)
        ? existingRows.map((row) => ({ ...row }))
        : [];
      const incoming = Array.isArray(incomingRows) ? incomingRows : [];
      incoming.forEach((incomingRow, i) => {
        if (!incomingRow || typeof incomingRow !== "object") return;
        const guestId = incomingRow.participant_guest_id;
        let idx = guestId
          ? existing.findIndex(
              (row) => row && String(row.participant_guest_id) === String(guestId)
            )
          : -1;
        if (idx === -1) idx = i;
        const scopedIncoming = {};
        Object.keys(incomingRow).forEach((k) => {
          if (!dataGridOwnKeys || dataGridOwnKeys.size === 0 || dataGridOwnKeys.has(k)) {
            scopedIncoming[k] = incomingRow[k];
          }
        });
        if (idx >= 0 && idx < existing.length) {
          existing[idx] = { ...existing[idx], ...scopedIncoming };
        } else {
          existing.push({ ...incomingRow });
        }
      });
      return existing;
    };

    let merged = mergeWith(
      {},
      saved,
      effectiveFormData,
      (objValue, src, key) =>
        Array.isArray(src)
          ? key === "dataGrid"
            ? mergeDataGridRowsClient(objValue, src)
            : cloneDeep(src)
          : undefined
    );
    merged = {
      ...merged,
      Group_id: selectedGroup || merged.Group_id || "",
      groupName: groupDoc?.group_name || merged.groupName || "",
    };

    if (schemaForSave) {
      merged = filterPersonDataForFormSave(merged, schemaForSave, saved);
    }

    if (profilePhoto) {
      merged = { ...merged, editProfilePhoto: [{ url: profilePhoto }] };
    }

    try {
      if (tabAtSave === 'GUEST') {
        const guestRows =
          (Array.isArray(formData?.dataGrid) && formData.dataGrid) ||
          (formData?.guestDetails &&
            Array.isArray(formData.guestDetails.dataGrid) &&
            formData.guestDetails.dataGrid) ||
          (formData?.guest &&
            Array.isArray(formData.guest.dataGrid) &&
            formData.guest.dataGrid) ||
          [];
        await axiosAuth.put(`${API_URL}/ems/participants/${id}/guests`, guestRows);
        delete merged.guestDetails;
        delete merged.guest;
        if (personDataHasCaddieFields(saved)) {
          copyCaddieFieldsFromSource(merged, saved);
        } else {
          delete merged.dataGrid;
        }
      }

      if (hasCaddie || personDataHasCaddieFields(saved)) {
        merged = preserveAndNormalizeCaddiesOnSave(merged, saved, {
          caddiesById: caddiesByIdRef.current,
          formio: formioWebformRef.current,
          isRegistrationTab: isRegistrationSave,
          liveSnapshot: latestFormDataRef.current,
          tabLabel: tabAtSave,
        });
      }

      const personForIdentity = participant?.person_id || {};
      preserveParticipantIdentityFromPerson(merged, personForIdentity);

      // If the Form.io invitation type dropdown was changed, sync it back to the header field
      const formInvitationType = merged?.invitationTypes || effectiveFormData?.invitationTypes || '';
      const effectiveInvitationType = formInvitationType || selectedInvitationType || null;
      if (formInvitationType && formInvitationType !== selectedInvitationType) {
        setSelectedInvitationType(formInvitationType);
      }

      await axios.put(`${API_URL}/ems/participants/${id}`, {
        person_data: merged,
        registration_status: registrationStatus,
        approval_status: approvalStatus,
        group_id: selectedGroup || null,
        invitation_type_id: effectiveInvitationType,
        role_id: selectedEmsRole || null,
        accreditation_category_id: selectedAccreditationCategory || null,
        progress_breakdown: tabProgress,
        admin_edit: true,
      });

      setParticipant((prev) =>
        prev
          ? {
              ...prev,
              person_data: merged,
              progress_breakdown: tabProgress,
              registration_status: registrationStatus,
              approval_status: approvalStatus,
              group_id: groupDoc || selectedGroup || prev.group_id,
              invitation_type_id: invitationTypes.find((t) => String(t._id) === String(effectiveInvitationType)) || effectiveInvitationType || null,
              role_id: emsRoles.find((r) => String(r._id) === String(selectedEmsRole)) || selectedEmsRole || null,
              accreditation_category_id: accreditationCategories.find(
                (c) => String(c._id) === String(selectedAccreditationCategory)
              ) || selectedAccreditationCategory || null,
            }
          : prev
      );
      latestFormDataRef.current =
        hasCaddie || personDataHasCaddieFields(merged)
          ? normalizeDataGridForFormioRender(JSON.parse(JSON.stringify(merged)))
          : merged;
      clearFormErrorsRef.current?.();
      setShowInlineErrors(false);
      await fetchParticipantData(true);
      if (!viaTabSwitch) {
        setSubmissionVersion((v) => v + 1);
        clearTimeout(pageMessageTimerRef.current);
        setPageMessage({
          type: "success",
          text: `${tabAtSave} saved successfully.`,
        });
        pageMessageTimerRef.current = setTimeout(() => setPageMessage(null), 3000);
      }
      return true;
    } catch (err) {
      console.error("Save failed:", err);
      if (!viaTabSwitch) {
        const apiMsg = err.response?.data?.message;
        setPageMessage({
          type: "error",
          text: apiMsg || "Failed to save. Please try again.",
        });
      }
      return false;
    } finally {
      isSavingRef.current = false;
    }
  };

  const handleSendEmail = async (emailBody, subjectOverride) => {
    if (sendingEmail) return;
    if (!selectedTemplateId) {
      setPageMessage({ type: "error", text: "Select an email template first." });
      return;
    }
    const personData = participant?.person_id;
    if (!personData?._id) {
      setPageMessage({ type: "error", text: "Person record missing for this participant." });
      return;
    }
    if (!personData?.email) {
      setPageMessage({ type: "error", text: "This person has no email on file." });
      return;
    }

    const chosenTemplate = templates.find((t) => t._id === selectedTemplateId);
    if (!chosenTemplate) {
      setPageMessage({ type: "error", text: "That template is no longer available. Refresh the page and try again." });
      return;
    }

    // Same path as Communication → Send Emails: POST /ems/send-emails
    const personId = normalizePersonId(personData?._id);
    const plainPassword =
      invitePlainPassword ||
      getOrCreateInvitePassword(personId, generateInvitePassword);
    const rawForSend =
      emailBody ||
      chosenTemplate.body ||
      chosenTemplate.templateBody ||
      chosenTemplate.content ||
      '';
    // Inject plain only on send (editor shows {{password}}); API bcrypt-hashes for DB
    const bodyToSend = replacePlaceholders(rawForSend, personData, plainPassword, {
      hidePassword: false,
    });
    // Same API as Communication → Send Emails. Server authenticates with .env SMTP_*.
    const emailPayload = {
      from_name: fromName,
      emails: [{
        personId: personData._id,
        subject: subjectOverride || chosenTemplate.subject || templateSubject || 'Notification',
        body: bodyToSend,
        plainPassword,
        attachments: templateFiles,
      }],
    };

    setSendingEmail(true);
    try {
      const sendRes = await axios.post(`${API_URL}/ems/send-emails`, emailPayload);
      const results = Array.isArray(sendRes.data?.results) ? sendRes.data.results : [];
      const failed = results.find((r) => r.status === 'error');
      if (failed) {
        setPageMessage({
          type: 'error',
          text: failed.message || sendRes.data?.message || 'Failed to send email.',
        });
        return;
      }
      try {
        const pendingRes = await axios.put(`${API_URL}/ems/participants/${id}/mark-pending`);
        const pendingStatus = pendingRes.data?.registration_status || 'Pending';
        setRegistrationStatus(pendingStatus);
        setParticipant((prev) =>
          prev ? { ...prev, registration_status: pendingStatus } : prev
        );
      } catch (pendingErr) {
        // Email already sent — status mark is best-effort (API also marks Added → Pending).
        console.warn('mark-pending after send failed', pendingErr);
      }
      clearTimeout(pageMessageTimerRef.current);
      setPageMessage({ type: "success", text: "Email sent." });
      pageMessageTimerRef.current = setTimeout(() => setPageMessage(null), 3000);
    } catch (err) {
      console.error('Send email failed', err);
      const apiMsg = err.response?.data?.message;
      setPageMessage({
        type: "error",
        text: apiMsg || "Failed to send email. Please try again.",
      });
    } finally {
      setSendingEmail(false);
    }
  };

  useEffect(() => {
    setPageMessage((prev) => {
      if (!prev?.validationItems?.length) return prev;
      return prev.errorTab === activeTab ? prev : null;
    });
  }, [activeTab]);

  const personIdForInvite = normalizePersonId(
    participant?.person_id?._id || participant?.person_id
  );

  useEffect(() => {
    if (!selectedTemplateId || !personIdForInvite) {
      if (!selectedTemplateId) {
        setTemplateName('');
        setTemplateSubject('');
        setTemplateFiles([]);
        setEditorHtml('');
        setInvitePlainPassword('');
      }
      return;
    }
    const selectedTemplate = templates.find((t) => t._id === selectedTemplateId);
    if (!selectedTemplate) return;
    const person = participant?.person_id;
    setTemplateName(selectedTemplate.name || '');
    setTemplateSubject(selectedTemplate.subject || '');
    const rawBody = selectedTemplate.body ?? selectedTemplate.templateBody ?? selectedTemplate.content ?? '';
    // Same cached plain as Send Emails; keep {{password}} in editor (hash only in DB after send)
    const pwd = getOrCreateInvitePassword(personIdForInvite, generateInvitePassword);
    setInvitePlainPassword(pwd);
    setEditorHtml(replacePlaceholders(rawBody, person, pwd, { hidePassword: true }));
    setTemplateFiles(Array.isArray(selectedTemplate.attachments) ? selectedTemplate.attachments : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- person fields read when personId is ready
  }, [selectedTemplateId, personIdForInvite, templates.length]);

  const SendEmailTabContent = () => {
    if (!templates.length) return <div className="p-3 text-muted">No templates available.</div>;
    return (
      <div className="p-3">
        <div className="mb-3">
          <label className="form-label fw-bold">Select Template</label>
          <select className="form-select" value={selectedTemplateId} onChange={(e) => setSelectedTemplateId(e.target.value)}>
            <option value="">-- Select Template --</option>
            {templates.map((t) => (
              <option key={t._id} value={t._id}>{t.name || t.subject || 'Untitled Template'}</option>
            ))}
          </select>
        </div>
        {selectedTemplateId && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSendEmail(editorHtml, templateSubject);
            }}
            className="border rounded p-3"
            style={{ background: '#fff' }}
          >
            <input
              type="text"
              className="form-control border-0 border-bottom mb-3"
              placeholder="Template Name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              required
            />
            <ReactQuill
              ref={quillRef}
              value={editorHtml}
              onChange={setEditorHtml}
              style={{ minHeight: '200px', marginBottom: '8px' }}
            />
            <div className="d-flex justify-content-end align-items-center gap-2 mt-3 pe-send-email-actions">
              <button
                type="submit"
                className="btn pe-send-email-submit"
                disabled={sendingEmail}
              >
                {sendingEmail ? 'Sending…' : 'Send Email'}
              </button>
              <button
                type="button"
                className="btn participant-edit-cancel-btn"
                disabled={sendingEmail}
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="user-form-loading" aria-busy="true" aria-label="Loading participant">
        <Spinner animation="border" role="status" style={{ color: '#d9a128', width: 36, height: 36 }} />
        <span className="user-form-loading__text">Loading participant…</span>
      </div>
    );
  }
  if (!participant) return <div className="p-4 text-center text-danger">Participant not found</div>;

  const person = participant?.person_id || {};
  const activeFormId = forms[activeTab];
  // schemaCacheTick: re-read cache after prefetch completes
  const cachedFormEntry = schemaCacheTick >= 0
    ? schemaCacheRef.current[activeFormId]
    : null;

  const bannerMessage =
    pageMessage && !pageMessage.validationItems?.length ? pageMessage : null;

  const displayName = `${person?.name || ''} ${person?.lname || ''}`.trim() || 'Participant';
  const selectedGroupLabel =
    groups.find((g) => String(g._id) === String(selectedGroup))?.group_name || '';
  const headerAvatarSrc =
    resolvePersonImageUrl(profilePhoto || person?.image, API_URL) ||
    profilePhoto ||
    '';
  const selectedRoleLabel =
    emsRoles.find((r) => String(r._id) === String(selectedEmsRole))?.role_name || '';
  const selectedInvitationLabel =
    invitationTypes.find((t) => String(t._id) === String(selectedInvitationType))?.name || '';
  const selectedAccreditationLabel = (() => {
    const cat = accreditationCategories.find(
      (c) => String(c._id) === String(selectedAccreditationCategory)
    );
    return cat ? formatAccreditationCategoryLabel(cat) : '';
  })();
  const isRegistered = String(registrationStatus || '') === 'Registered';
  const isApprovalPending =
    !approvalStatus || String(approvalStatus) === 'Pending';

  return (
    <div
      className={`participant-edit-page user-layout user-form-prototype${
        showInlineErrors ? ' user-form--show-errors' : ''
      }`}
    >
      {bannerMessage && (
        <div
          className={`participant-edit-toast alert ${
            bannerMessage.type === 'success' ? 'alert-info' : 'alert-danger'
          } position-fixed top-0 start-50 translate-middle-x mt-3 shadow`}
          role="status"
          aria-live="polite"
        >
          {bannerMessage.text}
        </div>
      )}

      <header className="user-form-context-row participant-edit-header">
        <section className="user-form-profile" aria-label="Participant summary">
          <div className="user-form-profile__identity">
            <div className="user-form-profile__avatar participant-edit-header__avatar">
              {headerAvatarSrc ? (
                <img src={headerAvatarSrc} alt="" />
              ) : (
                <FontAwesomeIcon icon={faUser} />
              )}
              <button
                type="button"
                className="user-form-profile__edit"
                aria-label="Upload profile photo"
                onClick={() => fileInputRef.current?.click()}
              >
                <FontAwesomeIcon icon={faPen} />
              </button>
              <input
                type="file"
                accept="image/*"
                hidden
                ref={fileInputRef}
                onChange={handlePhotoUpload}
              />
            </div>
            <div>
              <h1>{displayName}</h1>
            </div>
          </div>

          <div className="user-form-profile__metadata participant-edit-meta" aria-label="Assignment fields">
              <label
                className={`participant-edit-chip-select ${
                  isRegistered
                    ? 'participant-edit-chip-select--registered'
                    : 'participant-edit-chip-select--neutral'
                }`}
              >
                <FontAwesomeIcon icon={faCheckCircle} aria-hidden="true" />
                <span className="participant-edit-chip-select__prefix visually-hidden">
                  Registration Status
                </span>
                <select
                  id="pe-reg-status"
                  value={registrationStatus}
                  onChange={(e) => setRegistrationStatus(e.target.value)}
                  aria-label="Registration Status"
                >
                  <option value="Added">Added</option>
                  <option value="Pending">Pending</option>
                  <option value="Registered">Registered</option>
                </select>
              </label>

              <label
                className={`participant-edit-chip-select ${
                  isApprovalPending
                    ? 'participant-edit-chip-select--pending'
                    : 'participant-edit-chip-select--neutral'
                }`}
              >
                <FontAwesomeIcon icon={faClock} aria-hidden="true" />
                <select
                  id="pe-approval-status"
                  value={approvalStatus}
                  onChange={(e) => setApprovalStatus(e.target.value)}
                  aria-label="Approval Status"
                >
                  <option value="Pending">Pending Approval</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </label>

              <label className="participant-edit-chip-select participant-edit-chip-select--chip">
                <select
                  id="pe-group"
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                  aria-label="Group"
                  title={selectedGroupLabel || 'Select Group'}
                >
                  <option value="">Select Group</option>
                  {groups.map((g) => (
                    <option key={g._id} value={g._id}>
                      {g.group_name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="participant-edit-chip-select participant-edit-chip-select--chip">
                <select
                  id="pe-role"
                  value={selectedEmsRole}
                  onChange={(e) => setSelectedEmsRole(e.target.value)}
                  aria-label="Role"
                  title={selectedRoleLabel || 'Select Role'}
                >
                  <option value="">Select Role</option>
                  {emsRoles.map((r) => (
                    <option key={r._id} value={r._id}>
                      {r.role_name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="participant-edit-chip-select participant-edit-chip-select--chip">
                <select
                  id="pe-invitation"
                  value={selectedInvitationType}
                  onChange={(e) => setSelectedInvitationType(e.target.value)}
                  aria-label="Invitation Type"
                  title={selectedInvitationLabel || 'Select Invitation Type'}
                >
                  <option value="">Select Invitation Type</option>
                  {invitationTypes.map((t) => (
                    <option key={t._id} value={t._id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="participant-edit-chip-select participant-edit-chip-select--chip">
                <select
                  id="pe-accreditation"
                  value={selectedAccreditationCategory}
                  onChange={(e) => setSelectedAccreditationCategory(e.target.value)}
                  aria-label="Accreditation Category"
                  title={selectedAccreditationLabel || 'Select Accreditation Category'}
                >
                  <option value="">Select Accreditation Category</option>
                  {accreditationCategories.map((c) => (
                    <option key={c._id} value={c._id}>
                      {formatAccreditationCategoryLabel(c)}
                    </option>
                  ))}
                </select>
              </label>
          </div>
        </section>
      </header>

        <nav className="participant-edit-tabs" aria-label="Participant form sections">
          <div className="participant-edit-tabs__track" ref={tabsTrackRef}>
            <div
              ref={tabSliderRef}
              className="participant-edit-tabs__slider"
              aria-hidden="true"
            />
            {TABS.map((tab) => {
              const segment = TAB_PROGRESS_KEY[tab];
              const realPct = segment
                ? tabPercentFromProgress(tabProgress?.[segment])
                : null;
              const displayPct = segment != null ? (realPct == null ? 0 : realPct) : null;
              const isComplete = realPct === 100;
              return (
              <button
                key={tab}
                type="button"
                ref={(el) => {
                  if (el) tabBtnRefs.current[tab] = el;
                  else delete tabBtnRefs.current[tab];
                }}
                className={`participant-edit-tab${activeTab === tab ? ' participant-edit-tab--active' : ''}${
                  isComplete ? ' participant-edit-tab--complete' : ''
                }`}
                onClick={() => handleTabChange(tab)}
              >
                <span className="participant-edit-tab__label">{tabDisplayLabel(tab)}</span>
                {displayPct != null && (
                  <span
                    className="participant-edit-tab__pct"
                    aria-label={`${displayPct} percent complete`}
                  >
                    {displayPct}%
                  </span>
                )}
              </button>
              );
            })}
          </div>
        </nav>

        <div className="participant-edit-scroll">
        <div
          key={activeTab}
          className={`tab-content participant-form-body-enter ${participantFormTabClass(activeTab)}${
            showInlineErrors ? ' participant-form-tab--show-errors' : ''
          }`}
          ref={tabContentRef}
        >
          {showInlineErrors && activeFormId && activeTab !== 'SEND EMAIL' && activeTab !== 'BOOKING' && (
            <p className="user-form-inline-hint" role="alert">
              Please complete the highlighted required fields below.
            </p>
          )}
          {activeTab === 'SEND EMAIL' ? (
            <SendEmailTabContent />
          ) : activeTab === 'BOOKING' ? (
            <ParticipantBookingTab participant={participant} onNavigateToTab={handleTabChange} />
          ) : activeFormId ? (
            <>
            {activeTab === TABS[1] && (
              <AccreditationPhotoManager
                ref={photoManagerRef}
                headless
                participantId={participant?._id}
                currentPhotoUrl={accreditationPhotoUrls.currentPhotoUrl}
                originalPhotoUrl={accreditationPhotoUrls.originalPhotoUrl}
                onPhotoUpdated={handlePhotoManagerUpdate}
              />
            )}
            {/* Portal: buttons below the photo */}
            {photoFieldPortalEl && ReactDOM.createPortal(
              <div className="d-flex gap-2" style={{marginBottom: '10px', marginLeft: '45px'}}>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => photoManagerRef.current?.openView()}
                >
                  View
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  style={{ background: '#D9A128', color: '#fff', border: 'none' }}
                  onClick={() => photoManagerRef.current?.openCrop()}
                >
                  Edit / Crop
                </button>
              </div>,
              photoFieldPortalEl
            )}
            <FormioTabRenderer
              key={`${participant?._id || 'loading'}-${activeFormId}-v${submissionVersion}${
                activeTab === TABS[1] ? `-acc${accreditationKey}` : ''
              }`}
              formId={activeFormId}
              participantId={participant?._id}
              submission={initialSubmission}
              submissionVersion={submissionVersion}
              initialSchema={cachedFormEntry?.schema || null}
              initialFormName={cachedFormEntry?.form_name || ''}
              onDataChange={(data) => handleFormChange(activeTab, data)}
              API_URL={API_URL}
              liveDataRef={latestFormDataRef}
              flushLiveFormDataRef={flushLiveFormDataRef}
              formioWebformRef={formioWebformRef}
              triggerSaveRef={triggerSaveRef}
              triggerFormSubmitRef={triggerFormSubmitRef}
              clearFormErrorsRef={clearFormErrorsRef}
              onFormSave={handleFormSave}
              onValidationErrors={handleValidationErrors}
              showInlineErrors={showInlineErrors}
              formOptions={participantFormOptions}
              showProgressBadges
              onProgressStats={({ filled, total }) => {
                const segment = TAB_PROGRESS_KEY[activeTab];
                if (!segment) return;
                setTabProgress((prev) => {
                  const cur = prev[segment];
                  if (
                    Number(cur?.total) === total &&
                    Number(cur?.completed) === filled
                  ) {
                    return prev;
                  }
                  return {
                    ...prev,
                    [segment]: {
                      total,
                      completed: filled,
                      display: `${filled}/${total}`,
                    },
                  };
                });
              }}
              sharedCaddiesByIdRef={caddiesByIdRef}
              onFormSchemaLoad={(components) => {
                activeFormSchemaRef.current = components;
                hasRegistrationCaddieRef.current = formSchemaHasCaddieSelect(
                  components || []
                );
                const segment = TAB_PROGRESS_KEY[activeTab];
                const data =
                  latestFormDataRef.current ||
                  safeParsePersonData(participant?.person_data);
                if (segment && Array.isArray(components) && components.length) {
                  const { filled, total } = calculateVisibleFieldProgress(
                    data,
                    components,
                    formioWebformRef.current
                  );
                  setTabProgress((prev) => ({
                    ...prev,
                    [segment]: {
                      total,
                      completed: filled,
                      display: `${filled}/${total}`,
                    },
                  }));
                }
              }}
            />
            {activeTab === 'ACCOMMODATION' && <AccommodationSection bookingGroups={bookingGroups} />}
            </>
          ) : activeTab === 'PERSONAL SETTINGS' && personalSettingsResolving ? (
            <div className="user-form-loading user-form-loading--panel" aria-busy="true">
              <Spinner animation="border" role="status" style={{ color: '#d9a128', width: 32, height: 32 }} />
              <span className="user-form-loading__text">Loading form…</span>
            </div>
          ) : (
            <div className="p-3 text-muted">Form not found for this tab.</div>
          )}
        </div>

        {activeTab !== 'SEND EMAIL' && (
          <div className="mt-4 d-flex gap-2 justify-content-end participant-edit-footer-actions">
            <button
              type="button"
              className="btn participant-edit-cancel-btn"
              onClick={handleCancel}
            >
              Cancel
            </button>
            {activeFormId && (
              <button
                type="button"
                className="btn participant-edit-submit-btn"
                onClick={handleSubmit}
              >
                Save & Continue
              </button>
            )}
          </div>
        )}
        </div>
    </div>
  );
};

export default ParticipantEdit;